/**
 * AppDataGrid — the ONE shared data grid for every form in Alpaca Intelligence
 * (CLAUDE.md §18.6 / §7.5).  Forms pass only `rows`, `columns` and a little
 * config; the grid owns the styling, sorting, per-column filtering, the footer
 * (Part C) and the load-time indicator.  No form defines its own grid styling
 * or re-implements pagination — that is the whole point of standardising here.
 *
 * Enforced identically everywhere (see appGrid.css):
 *   • One visual style + one font size.
 *   • Vertical column separators on every row (not just the header).
 *   • Cell text BLACK by default; ONLY a `type:'status'` column is coloured
 *     (via the shared statusPill mapping).  IDs and every other field are black.
 *   • Light sort header (normal weight, lighter caret — never bolds).
 *   • Light-orange row hover (never blue).  Colourful filter icon.
 *   • Date / datetime columns render through the shared fmtLocal* helpers
 *     (§14b.6) — local timezone, 24-hour, no AM/PM, no inline date formatting.
 *
 * Performance: only the current page's rows are rendered to the DOM, so a
 * 10k-row dataset stays snappy (filter / sort run on the in-memory array).
 */
import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { ReactNode, MouseEvent as ReactMouseEvent, KeyboardEvent as ReactKeyboardEvent } from "react";
import { useSort } from "../SortableHeader";
import { statusVariant } from "../statusPill";
import { ColFilterButton, passesColumnFilter, cascadeRows, type ColumnFilterValue } from "../ColumnFilterDropdown";
import { fmtLocalDate, fmtLocalDateTime } from "../../utils/datetime";
import { LoaderSymbol } from "../loading";
// Shared "Export to" (standing rule 8) — imported from the file (not the AppToolbar
// index) so the grid never pulls a component that imports the grid back.
import { ExportMenu } from "../AppToolbar/ExportMenu";
import { useGridLayoutDefaults, moduleColumnWidth, MIN_DATA_COL_W } from "./gridLayoutContext";
import {
  autoFitWidth,
  boldFont,
  fontOf,
  DEFAULT_GRID_FONT,
  AUTOFIT_CELL_CHROME,
  AUTOFIT_HEADER_CHROME,
} from "./autoFitWidth";
import { InlineCellEditor } from "../gridEdit";
import { HeaderContextMenu } from "./HeaderContextMenu";
import { ColumnChooser } from "./ColumnChooser";
import { loadGridPrefs, saveGridPrefs } from "./gridPrefs";
import { GridPager } from "./GridPager";
import "./appGrid.css";

/** Column data type — drives default formatting, alignment and filtering. */
export type AppColType = "text" | "number" | "date" | "datetime" | "status";

export interface AppColumn<T> {
  /** Stable key — also the default property read from the row. */
  field: string;
  /** Header caption — may be a ReactNode for styled labels (e.g. red asterisk).
   *  When ReactNode is used, provide `headerText` (plain string) for export/filter. */
  header: string | ReactNode;
  /** Plain-text label used for CSV/Excel export headers and column-filter popup
   *  when `header` is a ReactNode.  Falls back to `header` when it is a string. */
  headerText?: string;
  /** Data type (default 'text').  'date'/'datetime' format via fmtLocal*. */
  type?: AppColType;
  width?: number | string;
  minWidth?: number | string;
  /** HARD upper bound on the column width. Under the grid's AUTO table-layout a
   *  column normally grows to its content (header/cells), ignoring `width`. Setting
   *  `maxWidth` (together with a matching `width`, and typically `minWidth`) pins the
   *  column to an exact width — the header/cells ellipsis-clip past it. Used to honor
   *  a configured column width verbatim (e.g. Sample Registration columns driven by
   *  Sample Matrix Field Setup Width). Opt-in per column. */
  maxWidth?: number | string;
  align?: "left" | "center" | "right";
  /** Wrap long cell text onto multiple lines instead of ellipsis-clipping it on
   *  one line (e.g. a free-text column like Sampling Equipment). Opt-in per
   *  column; pairs well with `fixedLayout` so one long value can't blow the
   *  column — and the whole table — wider than its container. */
  wrap?: boolean;
  /** Keep the cell on ONE line but DON'T ellipsis-clip it: the column grows to the
   *  full content width, so a long value (e.g. Sampling Equipment) extends the table
   *  and is reached via the grid's HORIZONTAL scroll bar instead of wrapping tall or
   *  being truncated. Opt-in per column; best on the LAST column so it can't overlap
   *  others. Mutually exclusive with `wrap`. */
  noClip?: boolean;
  /** CONTENT-BASED width (RTCFR "Dynamic Permit Limit Column Width", 2026-09-27):
   *  the column sizes to its longest visible text line — or its header, if wider —
   *  clamped to [min, max] px, recalculated whenever the rows or filters change.
   *  Past `max` the cell WRAPS (the full value stays visible; a renderer may add a
   *  tooltip). `lines` returns the text lines a row renders (a stacked cell = one
   *  entry per line); defaults to the column's display text. A user-dragged width
   *  still wins, and `width` is only the first-paint fallback. Opt-in per column. */
  autoFit?: { min: number; max: number; lines?: (row: T) => string[] };
  /** Default true.  Set false for action/custom columns. */
  sortable?: boolean;
  /** Default true (false when no header).  Filters on the displayed text. */
  filterable?: boolean;
  /** Set true to exclude this column from the shared Export (gridExportMatrix). */
  noExport?: boolean;
  /** Render a SUMMARY cell for this column in a footer row beneath the body —
   *  legacy XAF's grid summary (e.g. the Pending Item grid's "Sum=0.00" under
   *  ExtPrice). `sum` totals the column's numeric `value`; a function computes
   *  it from the rows. The footer row appears only when at least one column
   *  declares a summary, so existing grids are unaffected. */
  summary?: "sum" | ((rows: T[]) => string);
  /** Raw value override (defaults to `row[field]`). */
  value?: (row: T) => unknown;
  /** Custom cell renderer (e.g. progress bar, link).  Cell text stays black. */
  render?: (row: T) => ReactNode;
  /** Override the text used for filtering + sorting (defaults to the display text). */
  text?: (row: T) => string;
  /** Custom comparator for this column's sort (overrides the default value/text
   *  comparison). Takes two rows, returns <0 / 0 / >0. */
  compare?: (a: T, b: T) => number;
  /** Per-row extra class on the &lt;td&gt; — for a form that paints a cell from
   *  its DATA (e.g. Item Requisition tints Errorlog and Status on a row that
   *  failed validation, mirroring the legacy grid's client-side Init script).
   *  Opt-in: a column that does not set it is rendered exactly as before, so no
   *  existing grid changes. Put the colours in the FORM's own stylesheet; this
   *  hook only carries the class. */
  cellClass?: (row: T) => string | undefined;
  /** Right-click handler on the &lt;td&gt;.  Fires regardless of whether the column
   *  uses `edit` or `render` — attach context-menu logic here so it is not
   *  swallowed by the InlineCellEditor branch. */
  onCellContextMenu?: (e: ReactMouseEvent<HTMLTableCellElement>, row: T) => void;
  /** Opt-in INLINE EDITING (BO UI Standard §9 "pure grid, in-place editing").
   *  When set AND the grid is given `onCellCommit`, the cell becomes
   *  click-to-edit via the shared <InlineCellEditor> — no editor is drawn at
   *  rest; it opens on click, commits on blur/Enter, reverts on Escape. Existing
   *  grids that don't set `edit` are completely unaffected. */
  edit?: {
    type?: "text" | "combo" | "datetime" | "date" | "number" | "time";
    /** Options for the 'combo' editor — a static list or per-row. */
    options?: string[] | ((row: T) => string[]);
    trigger?: "click" | "double-click";
    /** Per-row read-only (e.g. a locked row) — renders text, never opens. */
    disabled?: (row: T) => boolean;
    /** Per-row required-yet-empty yellow tint (§9). */
    requiredEmpty?: (row: T) => boolean;
    /** `text` editor only: a regex (no delimiters) the committed value must
     *  match when non-empty — an invalid value is rejected, not saved. */
    pattern?: string;
    /** Shown as the invalid-value tooltip when `pattern` rejects the input. */
    patternMessage?: string;
    /** OPTIONAL — formats the cell's RESTING text (the editor edits the raw value),
     *  e.g. a price as $10.30. Opt-in; other editable cells are unchanged. */
    display?: (value: string) => string;
    /** 'combo' only: show the clear "✕" (default false — grid lookups are
     *  pick-from-list, matching legacy ALPACA XAF which has no clear button).
     *  Set true only for a combo cell that may legitimately be emptied. */
    clearable?: boolean;
    /** 'combo' only: also accept a TYPED value not in the list — a legacy combo
     *  with a free-text editor (e.g. the EAS Reference Library's Source /
     *  Storage). Default false (pick-from-list). Opt-in; nothing else changes. */
    allowCustom?: boolean;
  };
}

export interface AppDataGridProps<T> {
  rows: T[];
  columns: AppColumn<T>[];
  /** Stable row id (defaults to the row index). */
  getRowId?: (row: T, index: number) => string | number;
  loading?: boolean;
  /** @deprecated The load-time indicator now renders ONLY at the TOP of the
   *  form via the shared <FormTitle> chip (CLAUDE.md §18.6 — one location, top).
   *  Still accepted so existing callers compile, but the grid ignores it. */
  loadMs?: number | null;
  /** Default rows-per-page (default 20). */
  defaultPageSize?: number;
  /**
   * Hides the footer (page X of N · pager · page size).
   *
   * For a small grid that is being EDITED rather than browsed — a handful of
   * component rows, say — where "Page 1 of 1 (1 item)" under four rows is
   * noise. It does NOT disable paging; a caller that hides the footer should
   * also raise `defaultPageSize` so every row is reachable.
   */
  hideFooter?: boolean;
  /** Page-size dropdown options (default 10 / 20 / 50 / 100). */
  pageSizeOptions?: number[];
  /** Show a Maximize/Restore control on the grid's own header bar (ALPACA
   *  dashboard-grid parity). When on, a small header strip renders above the
   *  grid with an optional `gridTitle` on the left and a maximize icon on the
   *  right; clicking it fullscreens the grid (position:fixed), Esc/the icon
   *  restores. Off by default so existing grids are unchanged. */
  maximizable?: boolean;
  /** Fully suppress the grid's OWN header cluster (both the Export-to and the
   *  Maximize icons). Use this on a form that already renders its own
   *  export/maximize cluster elsewhere (e.g. Sample Transfer's status-filter row,
   *  the Result Validation/Approval/Tracking/Entry detail bars, the SDMS list) so
   *  the two icons don't appear twice. Since the export matrix is always built from
   *  the columns, `maximizable={false}` alone hides only the Maximize icon — the
   *  Export icon would still show — so this flag is the correct opt-out for
   *  "no built-in tools at all" (user report 2026-09-23). Off by default. */
  hideHeaderTools?: boolean;
  /** Hide ONLY the built-in Export icon (keep the Maximize icon + header strip).
   *  For a form that renders its OWN labelled "Export to" control in `headerLeft`
   *  (e.g. Barcode Sample Custody, legacy parity) and must not show the built-in
   *  Export icon as well — otherwise the grid would have two export controls.
   *  Off by default so existing grids are unchanged. */
  hideHeaderExport?: boolean;
  /** Optional caption shown on the left of the maximizable header strip (e.g.
   *  "Result Approval"). Rendered when the header strip is shown (i.e.
   *  `maximizable` OR `exportFilename`). */
  gridTitle?: string;
  /** Optional custom content rendered on the LEFT of the grid's header strip —
   *  e.g. a form's Search box or filter chips — so it sits on the SAME row as the
   *  Export + Maximize icons instead of in a separate strip above the grid (the
   *  ModuleListView search-row look). Rendered after `gridTitle`, before the
   *  right-aligned tools. Off by default (existing grids unchanged). */
  headerLeft?: ReactNode;
  /** CONTROLLED maximize (optional). When provided, the grid's fullscreen state is
   *  driven by the caller instead of the internal Maximize button — used by a form
   *  that renders its OWN maximize control elsewhere (e.g. a ModuleListView-style
   *  search row above the card) together with `hideHeaderTools`. Leave undefined
   *  for the default self-managed behaviour (existing grids unchanged). Esc still
   *  restores and calls `onMaximizeChange(false)`. */
  maximized?: boolean;
  onMaximizeChange?: (v: boolean) => void;
  /** Show a shared "Export to" (share icon) control on the grid's own header
   *  strip — the SAME `<ExportMenu>` every form uses (standing rule 8: full
   *  ALPACA format set, Save-As picker, global loader, "Exported to X
   *  successfully."). The export acts on the grid's OWN filtered+sorted rows and
   *  visible columns (WYSIWYG). Set this to the desired base filename to enable
   *  it. Pairs with `maximizable` to give the SDMS-style export+maximize cluster
   *  at the grid's top-right. Off by default (existing grids unchanged). */
  exportFilename?: string;
  onRowClick?: (row: T) => void;
  /** Fires on a row double-click (e.g. open the record for edit). */
  onRowDoubleClick?: (row: T) => void;
  /** Fired when the pointer ENTERS a row. Intended for prefetching whatever the
   *  row opens, so the click lands on data that is already there. Must stay cheap
   *  and invisible: it fires on mere hover, not on intent. */
  onRowHover?: (row: T) => void;
  selectedRowId?: string | number | null;
  /** Show a leading checkbox column for multi-row selection (requires getRowId). */
  selectable?: boolean;
  /** Controlled set of selected row ids (used with `selectable`). */
  selectedIds?: Set<string | number>;
  /** Called with the next full selection Set whenever it changes. */
  onSelectionChange?: (ids: Set<string | number>) => void;
  /** When `selectable` is on, suppress the checkbox column entirely and let a
   *  row CLICK toggle its own selection instead (still drives `selectedIds` /
   *  `onSelectionChange` and the `adg-selected` row highlight) — for designs
   *  that select-to-act without a visible checkbox (e.g. a prototype's
   *  click-a-row rollback picker). Only meaningful together with `selectable`. */
  hideCheckboxColumn?: boolean;
  /** OPT-IN: paint the selected row in the theme ACCENT (a tint plus a left
   *  accent bar) instead of the app-wide neutral selection colour, which is too
   *  faint to read as "this is the one I picked". For single-pick grids whose
   *  only job is choosing ONE row before an OK (e.g. Reagent Prep's Copy
   *  Previous). Unset = every existing grid unchanged. */
  accentSelection?: boolean;
  /** OPT-IN: a FILTER ROW under the header — one text box per column; typing
   *  keeps the rows whose DISPLAYED text contains it (case-insensitive, same text
   *  the cell shows, so a date filters as MM/DD/YYYY). Combines with the header
   *  filter popups. For look-up grids where the user hunts for one row (e.g.
   *  Reagent Prep's Copy Previous). A column with `filterable: false` gets no box.
   *  Unset = every existing grid unchanged. */
  filterRow?: boolean;
  /** Freeze (pin) the first N DATA columns so they stay visible while the rest
   *  of the grid scrolls horizontally (BO UI Standard — "sticky first column",
   *  §7.1). The leading checkbox column (when `selectable`) is pinned too. Each
   *  frozen column MUST carry a numeric `width` so the left offsets compute
   *  correctly. Default 0 = no frozen columns (existing grids unaffected). */
  stickyColumns?: number;
  /** Inline-edit commit (§9). Required for any column with `edit` to be
   *  editable — called with the row, the column, and the committed string. */
  onCellCommit?: (row: T, column: AppColumn<T>, newValue: string) => void;

  /** ROW-level inline editing — legacy XAF `InlineEditMode="Inline"` (as opposed
   *  to the per-cell click-to-edit above, which is XAF's "Batch"). A leading
   *  PENCIL column puts the WHOLE row into editors; the pencil becomes Save +
   *  Undo. Only columns that declare `edit` become editable, and only while
   *  their own row is in edit mode — so the grid is NOT click-to-edit at rest.
   *  Opt-in: a grid that does not pass this is completely unaffected. */
  rowEdit?: {
    /** Commit the row. `values` is keyed by column `field`, holding only the
     *  editable columns. Throw to keep the row in edit mode. */
    onSave: (row: T, values: Record<string, string>) => void | Promise<void>;
    /** Per-row opt-out — e.g. a record that has already been actioned. */
    disabled?: (row: T) => boolean;
  };
  /** Optional: fires on every keystroke WHILE a text/combo cell is still being
   *  edited (does not close the cell) — wire this into a batch-edit buffer so
   *  "dirty" state (and anything gated on it, e.g. a Save button) updates the
   *  moment the user types, without depending on blur/Enter firing first.
   *  `onCellCommit` still fires as usual when the edit finishes. */
  onCellChange?: (row: T, column: AppColumn<T>, newValue: string) => void;
  /** Fires with the current filtered + sorted row set (pre-pagination) whenever
   *  it changes — i.e. after the grid's OWN per-column header filters are
   *  applied, not just the caller's `rows`.  Wire this into the form's
   *  `AppToolbar exportData` (via `gridExportMatrix`) so "Export to" exports
   *  exactly what the grid is showing, including any active column filter —
   *  otherwise an export silently includes rows a column filter hid. */
  onVisibleRowsChange?: (rows: T[]) => void;
  /** Show the per-column filter row (default true). */
  showFilters?: boolean;
  emptyText?: string;
  loadingText?: string;
  className?: string;
  /**
   * Let the table span the permanently-reserved 9px scrollbar gutter, so its
   * content reaches the grid's right border.
   *
   * The scroll area reserves that gutter (`scrollbar-gutter: stable`) to stop a
   * grid reflowing when a filter toggles the vertical scrollbar. A table that
   * FITS therefore stops 9px short of the border, while one that OVERFLOWS
   * paints straight across the gutter — so stacking a wide grid above a narrow
   * one leaves their content edges 9px apart even though the boxes are
   * pixel-identical. Set this on the narrow grid of such a pair.
   *
   * Never worse than the default: when the grid genuinely needs a vertical
   * scrollbar, the bar occupies exactly the 9px the gutter was reserving.
   */
  flushRight?: boolean;
  /** Initial sort column + direction, e.g. { key: 'name', dir: 'asc' }. */
  initialSort?: { key: string; dir: "asc" | "desc" };
  /** Opt-in per-row CSS class (e.g. a domain "new / modified" indicator that
   *  mirrors a legacy ConditionalAppearance rule, like MOR Adhoc's Bench QC or
   *  MSR's ALPACA-parity green "newly retrieved" row). Leave unset for the
   *  default grid look — CLAUDE.md §18.6 still applies: only a `type:'status'`
   *  column colours by default; this exists for the rare case where a whole
   *  ROW (not a column) must carry legacy's row-level semantic colour. */
  getRowClassName?: (row: T) => string | undefined;
  /** When set, this grid's column visibility / order / widths / sort persist to
   *  localStorage under this key and restore on the next visit. Omit for the
   *  default in-memory-only behaviour. Must be UNIQUE per grid (e.g. a form id). */
  persistKey?: string;
  /** Use `table-layout: fixed` so the table is ALWAYS exactly its container width:
   *  columns keep their configured widths (proportionally), a long cell can never
   *  push the table wider than its container, and there is no horizontal scroll.
   *  Pairs with per-column `wrap` for a free-text column. Opt-in (default auto). */
  fixedLayout?: boolean;
  /** Honor each column's numeric `width` as an EXACT width (pins width+min+max),
   *  so the AUTO table-layout can't grow a column to its header/content. The grid
   *  keeps horizontal scroll (unlike `fixedLayout`, which forces the table to the
   *  container width). Header/cells ellipsis-clip past the width; a user drag still
   *  overrides. Used where columns carry meaningful configured widths (e.g. Sample
   *  Registration driven by Sample Matrix Field Setup). Opt-in (default off). */
  enforceWidths?: boolean;
  /** Let header labels WRAP onto multiple lines instead of ellipsis-clipping on
   *  one line. Useful in a width-constrained container (e.g. a popup clamped by
   *  the viewport) so multi-word headers ("Sample Matrix", "Sampling Equipment")
   *  stay fully readable in a narrow column. Opt-in (default single-line). */
  wrapHeaders?: boolean;
  /** Auto-focus the FIRST row once, when data first loads, so an interactive grid
   *  is immediately keyboard-drivable on form open (arrow keys work without first
   *  Tabbing/clicking into the grid). Default true. It never steals focus from an
   *  editable field the page already focused (e.g. a search box), only fires once,
   *  and applies only to keyboard-navigable grids. Pass false on a page that must
   *  keep initial focus elsewhere. */
  autoFocusFirstRow?: boolean;
  /** Optional hint in the footer, just before "Page size:" (e.g. "Double-click a row
   *  to open it."). Opt-in — omitted, the footer is unchanged. */
  footerNote?: ReactNode;
  /** OPTIONAL — MASTER–DETAIL (shared opt-in, 2026-10-08; first user: the PLM
   *  Reference Library, whose slides hold one child row per asbestos type).
   *
   *  Adds a narrow "+ / −" column after the selection checkbox; an expanded row
   *  is followed by ONE full-width row holding whatever `render` returns. The
   *  "±" in the header opens every row of the current (filtered) list, or closes
   *  them when all are already open. On a focused row, "+" opens and "−" closes.
   *
   *  CONTROLLED: the caller owns the open set, so it survives a reload and other
   *  controls (a count chip in a cell, …) can toggle the same row. Needs
   *  `getRowId`. Omitted, the grid is unchanged. Not combined with
   *  `stickyColumns`. */
  rowDetail?: {
    /** What an open row shows, in one full-width cell. Not needed with `childRows`. */
    render?: (row: T) => ReactNode;
    /** G5 GROUPED GRID (opt-in, first user: Parameter Default 2026-10-14). The row is drawn
     *  as ONE full-width cell holding this label (the group heading) instead of its
     *  column cells — the grid's header then names the CHILD rows' columns. */
    groupLabel?: (row: T) => ReactNode;
    /** G5 GROUPED GRID: an open row is followed by these rows, drawn as ordinary rows
     *  under the grid's OWN columns (read-only), instead of the `render` cell. */
    childRows?: (row: T) => T[];
    expandedIds: ReadonlySet<string | number>;
    onExpandedChange: (ids: Set<string | number>) => void;
    /** Tooltip of a collapsed row's "+" (default "Show details"). */
    expandTitle?: string;
    /** Leave the header cell empty — no "±" expand / collapse-all button (legacy grids
     *  that have none, e.g. Parameter Default). Unset = the button shows. */
    hideExpandAll?: boolean;
  };
}

// 20 = the app-wide default page size (user decision 2026-09-24, superseding the
// 2026-07-02 "keep 100" call — see components/listDefaults.ts DEFAULT_PAGE_SIZE,
// which this mirrors, and CLAUDE.md §18.6). A form that genuinely needs a
// different default still passes `defaultPageSize`; the user changes it per grid
// from the footer dropdown.
const DEFAULT_PAGE_SIZE = 20;
// App-wide page-size dropdown, GoLive parity (matches the Client-picker set the
// user referenced 2026-08-03). 10 is kept because several small grids default to
// it; the high sizes (200/500/1000/2000) mirror GoLive's lookup popups. This is
// the single source — forms no longer pass their own pageSizeOptions.
const DEFAULT_PAGE_SIZES = [10, 20, 50, 100, 200, 500, 1000, 2000];

function rawValue<T>(row: T, col: AppColumn<T>): unknown {
  if (col.value) return col.value(row);
  return (row as Record<string, unknown>)[col.field];
}

/** The text shown in the cell (also used for filtering + text/status sorting). */
function displayText<T>(row: T, col: AppColumn<T>): string {
  if (col.text) return col.text(row);
  const v = rawValue(row, col);
  if (v === null || v === undefined) return "";
  switch (col.type) {
    case "date":
      return fmtLocalDate(v as string);
    case "datetime":
      return fmtLocalDateTime(v as string);
    default:
      return String(v);
  }
}

/**
 * Build the export matrix (headers + display-formatted string cells) for a set
 * of rows against the grid's columns — the ONE shared way to feed <ExportMenu>
 * / <AppToolbar exportData>.  Uses the SAME displayText as the grid cells, so the
 * export matches what's on screen (dates via fmtLocal*, col.text/value honoured).
 * Pass the form's FULL filtered dataset (not the visible page) to export all rows.
 * A column can opt out of export with `noExport: true` (e.g. a checkbox column).
 */
// eslint-disable-next-line react-refresh/only-export-components
export function gridExportMatrix<T>(
  rows: readonly T[],
  columns: AppColumn<T>[],
): { headers: string[]; rows: string[][] } {
  const cols = columns.filter((c) => !c.noExport);
  return {
    headers: cols.map((c) => c.headerText ?? (typeof c.header === "string" ? c.header : "")),
    rows: rows.map((r) => cols.map((c) => displayText(r, c))),
  };
}

/** Value used for sorting — raw for number/date so the comparator stays smart. */
function sortValue<T>(row: T, col: AppColumn<T>): unknown {
  if (col.type === "number" || col.type === "date" || col.type === "datetime") {
    return rawValue(row, col);
  }
  return displayText(row, col);
}

export function AppDataGrid<T>(props: AppDataGridProps<T>) {
  const {
    rows,
    columns: columnsProp,
    getRowId,
    loading = false,
    // `loadMs` is intentionally NOT consumed here: the load-time indicator now
    // lives ONLY at the TOP of the form (shared <FormTitle> chip), never in the
    // grid footer (CLAUDE.md §18.6 — one location, top).  The prop is kept on
    // the interface for source-compat with callers that still pass it.
    defaultPageSize: defaultPageSizeProp,
    hideFooter = false,
    pageSizeOptions = DEFAULT_PAGE_SIZES,
    // Both header icons (export + maximize) now default ON for EVERY grid
    // (user request 2026-09-23, "add the 2 symbols to every grid globally").
    // A caller can still opt out with maximizable={false}.
    maximizable = true,
    hideHeaderTools = false,
    hideHeaderExport = false,
    gridTitle,
    headerLeft,
    maximized: maximizedProp,
    onMaximizeChange,
    exportFilename,
    onRowClick,
    onRowDoubleClick,
    onRowHover,
    selectedRowId = null,
    accentSelection = false,
    filterRow = false,
    selectable = false,
    selectedIds,
    onSelectionChange,
    hideCheckboxColumn = false,
    stickyColumns = 0,
    onCellCommit,
    rowEdit,
    onCellChange,
    showFilters = true,
    emptyText = "No records to display",
    loadingText = "Loading…",
    className,
    onVisibleRowsChange,
    initialSort,
    getRowClassName,
    persistKey,
    fixedLayout = false,
    enforceWidths: enforceWidthsProp = false,
    flushRight = false,
    wrapHeaders = false,
    autoFocusFirstRow = true,
    footerNote,
    rowDetail,
  } = props;

  // Module-wide column width (Data Center = G5 ListView 140px, 2026-10-13):
  // every DATA column is pinned to that exact width via the existing
  // enforceWidths path. Icon/action columns (< MIN_DATA_COL_W) keep theirs, and
  // a fixedLayout grid keeps its proportional columns.
  const layout = useGridLayoutDefaults();
  const moduleCols = !fixedLayout && layout.fixedColumnWidth !== undefined;
  const columns = useMemo(
    () => !moduleCols
      ? columnsProp
      : columnsProp.map((c) =>
          typeof c.width === "number" && c.width < MIN_DATA_COL_W
            ? c
            : { ...c, width: moduleColumnWidth(layout, c.field), minWidth: undefined, maxWidth: undefined }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnsProp, moduleCols, layout],
  );
  const enforceWidths = enforceWidthsProp || moduleCols;
  // Rows per page: the form's own default, else the module default (Data
  // Center = 100), else the app-wide 20.
  const defaultPageSize = defaultPageSizeProp ?? layout.pageSize ?? DEFAULT_PAGE_SIZE;

  // Saved per-grid prefs (column layout + sort), loaded once. Empty when no
  // persistKey or nothing saved yet — so unpersisted grids behave exactly as before.
  const savedPrefs = useMemo(() => loadGridPrefs(persistKey), [persistKey]);
  const sorter = useSort<T>(
    savedPrefs.sort?.key ?? initialSort?.key ?? null,
    savedPrefs.sort?.dir ?? initialSort?.dir ?? null,
  );
  // Default filter-list direction (CLAUDE.md §18.6, 2026-06-18): when a column
  // is NOT actively click-sorted, its header-filter still lists values in the
  // grid's natural order — DESC when the grid's first column is an ID (records
  // are shown newest-first by that ID), otherwise ASC.  Header "…ID" (e.g.
  // JobID) or field "…Id" (jobId) marks an ID column.  An actively click-sorted
  // column overrides this with its own direction (see the ColFilterButton below).
  const defaultFilterDir: "asc" | "desc" = useMemo(() => {
    const first = columns[0];
    const firstHeader = typeof first?.header === "string" ? first.header : (first?.headerText ?? "");
    const isId = !!first && (/ID$/.test(firstHeader) || /Id$/.test(first.field ?? ""));
    return isId ? "desc" : "asc";
  }, [columns]);
  const [filters, setFilters] = useState<Record<string, ColumnFilterValue>>({});
  /** Filter-row text per column (opt-in `filterRow`). */
  const [rowFilterText, setRowFilterText] = useState<Record<string, string>>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  // Maximize/Restore (grid-control feature, ALPACA parity) — fullscreens the
  // grid root; Esc restores. Self-managed by default; a caller can CONTROL it via
  // the `maximized` / `onMaximizeChange` props (e.g. a form driving it from its own
  // search-row maximize button, with hideHeaderTools).
  const [maximizedInternal, setMaximizedInternal] = useState(false);
  const maximized = maximizedProp ?? maximizedInternal;
  const setMaximized = (v: boolean) => {
    if (maximizedProp !== undefined) onMaximizeChange?.(v);
    else setMaximizedInternal(v);
  };
  useEffect(() => {
    if (!maximized) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMaximized(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maximized]);
  // User-dragged column widths (override col.width).  Every column is resizable.
  const [colWidths, setColWidths] = useState<Record<string, number>>(() => savedPrefs.widths ?? {});
  // True while a resize drag is in progress. The header <th> is ALSO draggable
  // (column reorder), so onDragStart checks this to NOT start a reorder when the
  // user grabbed the resize handle.
  const resizingRef = useRef(false);
  const startResize = (field: string, e: ReactMouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    resizingRef.current = true;
    const th = (e.currentTarget as HTMLElement).parentElement as HTMLElement;
    const startX = e.clientX;
    const startW = th.getBoundingClientRect().width;
    const onMove = (ev: MouseEvent) =>
      setColWidths((prev) => ({ ...prev, [field]: Math.max(60, startW + (ev.clientX - startX)) }));
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      // Clear on the next tick so a dragstart that fires right after mouseup
      // still sees the resize flag and cancels the reorder.
      setTimeout(() => {
        resizingRef.current = false;
      }, 0);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  // ── Column management: hide / show-all / chooser / drag-reorder ─────────────
  // Hidden columns (by field) + a user column order, both in-memory per grid
  // instance (like colWidths above). Every column can be resized (handle),
  // reordered (drag a header), hidden (header menu / chooser) and restored.
  const [hidden, setHidden] = useState<Set<string>>(() => new Set(savedPrefs.hidden ?? []));
  const [order, setOrder] = useState<string[] | null>(() => savedPrefs.order ?? null);
  // Column Chooser popup anchor (viewport coords) or null when closed.
  const [chooser, setChooser] = useState<{ x: number; y: number } | null>(null);
  // Persist column layout + sort whenever any of them change (best-effort; no-op
  // without a persistKey).
  useEffect(() => {
    saveGridPrefs(persistKey, {
      hidden: [...hidden],
      order: order ?? undefined,
      widths: colWidths,
      sort: { key: sorter.sort.key, dir: sorter.sort.dir },
    });
  }, [persistKey, hidden, order, colWidths, sorter.sort]);
  // Header being dragged / dragged-over, for the reorder drop indicator.
  const [dragField, setDragField] = useState<string | null>(null);
  const [dragOverField, setDragOverField] = useState<string | null>(null);

  // Header right-click context menu (Sort · … more features land here one at a
  // time): the field of the right-clicked column + the cursor position, or null
  // when closed.
  const [headerMenu, setHeaderMenu] = useState<{ field: string; x: number; y: number } | null>(null);

  const colByField = useMemo(() => {
    const m = new Map<string, AppColumn<T>>();
    for (const c of columns) m.set(c.field, c);
    return m;
  }, [columns]);

  /** Plain-text header label for a column (menu title, export, filter). */
  const headerLabel = (col: AppColumn<T>): string =>
    col.headerText ?? (typeof col.header === "string" ? col.header : col.field);

  // Columns in the user's chosen order (unknown / newly-added fields keep their
  // natural position at the end), then filtered to the non-hidden set — this is
  // the list the grid actually renders (header + body + sticky + colCount).
  const orderedColumns = useMemo(() => {
    if (!order) return columns;
    const idx = new Map(order.map((f, i) => [f, i] as const));
    return [...columns].sort((a, b) => (idx.get(a.field) ?? 1e9) - (idx.get(b.field) ?? 1e9));
  }, [columns, order]);
  const visibleColumns = useMemo(() => orderedColumns.filter((c) => !hidden.has(c.field)), [orderedColumns, hidden]);

  // A grid always shows ≥1 column — the "keep one visible" guard is computed from
  // `prev` inside each setter so it never trips on a stale closure.
  const visibleCountAfter = (prev: Set<string>) => orderedColumns.filter((c) => !prev.has(c.field)).length;
  const hideColumn = (field: string) =>
    setHidden((prev) => {
      if (visibleCountAfter(prev) <= 1) return prev;
      const next = new Set(prev);
      next.add(field);
      return next;
    });
  const showAllColumns = () => setHidden(new Set());
  const toggleColumn = (field: string) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(field)) next.delete(field);
      else if (visibleCountAfter(prev) > 1) next.add(field);
      return next;
    });
  const reorderColumns = (from: string, to: string) => {
    if (!from || from === to) return;
    setOrder(() => {
      const cur = orderedColumns.map((c) => c.field);
      const fi = cur.indexOf(from);
      if (fi < 0) return cur;
      cur.splice(fi, 1);
      const ti = cur.indexOf(to);
      cur.splice(ti < 0 ? cur.length : ti, 0, from); // drop BEFORE the target
      return cur;
    });
  };

  // Excel-style cascade: a column's filter dropdown lists only the values that
  // pass the OTHER active column filters (the edited column is excluded), so
  // picking one column narrows the rest.  `cascadeRows` returns `rows` unchanged
  // (no copy) when no other filter is active, so this is free in the common case.
  const cascadeText = (row: T, key: string): string => {
    const c = colByField.get(key);
    return c ? displayText(row, c) : "";
  };

  // Filter → sort → (paginate happens after).  Per-column filtering uses the
  // shared Excel-style multi-select dropdown (ColumnFilterDropdown), matching on
  // the displayed text so a date column filters by exactly what the cell shows
  // (§14b.6).  Same UX as every legacy grid.
  const filtered = useMemo(() => {
    const active = Object.entries(filters).filter(([, v]) => v !== null);
    const typed = filterRow
      ? Object.entries(rowFilterText)
          .map(([field, q]) => [field, q.trim().toLowerCase()] as const)
          .filter(([, q]) => q !== "")
      : [];
    if (active.length === 0 && typed.length === 0) return rows;
    return rows.filter((row) =>
      active.every(([field, v]) => {
        const col = colByField.get(field);
        if (!col) return true;
        return passesColumnFilter(v, displayText(row, col));
      }) &&
      typed.every(([field, q]) => {
        const col = colByField.get(field);
        return !col || displayText(row, col).toLowerCase().includes(q);
      }),
    );
  }, [rows, filters, colByField, filterRow, rowFilterText]);

  // Auto-fit columns (`col.autoFit`) — measured in the grid's REAL font (read from
  // the rendered table once it mounts) over every row that passes the filters, so
  // the width follows the data on load / refresh / filter / job change and is
  // stable across pages. Shared calculator: ./autoFitWidth.
  const tableRef = useRef<HTMLTableElement>(null);
  const [gridFont, setGridFont] = useState(DEFAULT_GRID_FONT);
  useLayoutEffect(() => {
    const f = fontOf(tableRef.current);
    setGridFont((prev) => (prev === f ? prev : f));
  }, []);
  const autoWidths = useMemo(() => {
    const out: Record<string, number> = {};
    for (const col of visibleColumns) {
      if (!col.autoFit) continue;
      const lines: string[] = [];
      for (const row of filtered) {
        const ls = col.autoFit.lines ? col.autoFit.lines(row) : [displayText(row, col)];
        for (const l of ls) if (l) lines.push(l);
      }
      out[col.field] = autoFitWidth({
        header: typeof col.header === "string" ? col.header : (col.headerText ?? col.field),
        lines,
        font: gridFont,
        headerFont: boldFont(gridFont),
        min: col.autoFit.min,
        max: col.autoFit.max,
        cellChrome: AUTOFIT_CELL_CHROME,
        headerChrome: AUTOFIT_HEADER_CHROME,
      });
    }
    return out;
  }, [visibleColumns, filtered, gridFont]);
  /** The width a column renders at: user-dragged > auto-fit > configured. */
  const effectiveWidth = (col: AppColumn<T>): number | string | undefined =>
    colWidths[col.field] ?? autoWidths[col.field] ?? col.width;

  const sorted = useMemo(() => {
    // A column may supply its own comparator (compare); otherwise fall back to
    // the default smart value/text sort.
    const { key, dir } = sorter.sort;
    const col = key ? colByField.get(key) : undefined;
    if (key && dir && col?.compare) {
      const mult = dir === "asc" ? 1 : -1;
      return filtered.slice().sort((a, b) => mult * col.compare!(a, b));
    }
    return sorter.apply(filtered, (row, k) => {
      const c = colByField.get(k);
      return c ? sortValue(row, c) : (row as Record<string, unknown>)[k];
    });
  }, [filtered, sorter, colByField]);

  // Report the live filtered+sorted set (pre-pagination) so a caller's export
  // (AppToolbar exportData via gridExportMatrix) reflects the grid's OWN
  // column filters, not just whatever `rows` the caller happened to pass in.
  // `onVisibleRowsChange` intentionally excluded from deps — an inline arrow
  // function identity must not retrigger this on every render.
  useEffect(() => {
    onVisibleRowsChange?.(sorted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorted]);

  // Header "Export to" payload — the grid's OWN filtered+sorted rows against its
  // VISIBLE columns, so the export is exactly what's on screen (standing rule 8).
  // Export icon shows on EVERY grid now (user request 2026-09-23, global) — the
  // matrix is always built from the current columns/rows; the filename falls
  // back to the grid title (else "grid-export") when no explicit one is passed.
  const effExportFilename =
    exportFilename ??
    (gridTitle ? gridTitle.replace(/[^\w-]+/g, "_").replace(/^_+|_+$/g, "") || "grid-export" : "grid-export");
  const headerExport = useMemo(() => gridExportMatrix(sorted, visibleColumns), [sorted, visibleColumns]);

  const total = sorted.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);

  // Snap back to page 1 whenever filters or page size change.
  useEffect(() => setPage(1), [filters, rowFilterText, pageSize]);

  const pageRows = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, safePage, pageSize]);

  const setColFilter = (field: string, v: ColumnFilterValue) => setFilters((prev) => ({ ...prev, [field]: v }));

  // ── Optional multi-row selection (checkbox column) ──
  const rowKey = (row: T, i: number): string | number => (getRowId ? getRowId(row, i) : i);
  // Select-all toggles every FILTERED row (across pages), the usual bulk-action scope.
  // G5 grouped grid (rowDetail.childRows): what is selected is the CHILD rows, never the
  // group headings, so select-all covers every child of every (filtered) group.
  const groupedRows = !!rowDetail?.childRows;
  const allIds = useMemo(
    () =>
      !selectable
        ? []
        : groupedRows
          ? sorted.flatMap((g) => rowDetail!.childRows!(g)).map((r, i) => rowKey(r, i))
          : sorted.map((r, i) => rowKey(r, i)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectable, sorted, getRowId, groupedRows],
  );
  const allChecked = selectable && allIds.length > 0 && !!selectedIds && allIds.every((id) => selectedIds.has(id));
  const someChecked = selectable && !!selectedIds && allIds.some((id) => selectedIds.has(id));
  const toggleAll = () => {
    if (!onSelectionChange) return;
    const next = new Set(selectedIds ?? []);
    // If ANYTHING in the current (filtered) set is selected, a header click
    // CLEARS it — a reliable "uncheck all".  Only when nothing is selected does
    // it select all.  (Previously it cleared only when EVERY row was selected,
    // so grids that cap selection via a guard — e.g. Custom Reporting's
    // same-client rule — could never reach allChecked and thus could never
    // uncheck from the header.  2026-06-28.)
    if (allChecked || someChecked) allIds.forEach((id) => next.delete(id));
    else allIds.forEach((id) => next.add(id));
    onSelectionChange(next);
  };
  const toggleOne = (id: string | number) => {
    if (!onSelectionChange) return;
    const next = new Set(selectedIds ?? []);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectionChange(next);
  };

  const showCheckboxColumn = selectable && !hideCheckboxColumn;
  // In a grouped grid the checkbox column comes AFTER the expand column (G5 order), so the
  // usual leading one is not drawn; `groupCb` draws it in its G5 place instead.
  const leadCb = showCheckboxColumn && !groupedRows;
  const groupCb = showCheckboxColumn && groupedRows;
  // When `selectable` runs without a checkbox column, a row CLICK toggles its own
  // selection — the keyboard Space/Enter path mirrors that below.
  const rowClickToggles = selectable && hideCheckboxColumn;
  const colCount = visibleColumns.length + (showCheckboxColumn ? 1 : 0) + (rowDetail ? 1 : 0);

  // Master–detail (opt-in `rowDetail`): the caller owns the open set.
  const setDetailOpen = (id: string | number, open: boolean) => {
    if (!rowDetail) return;
    const next = new Set(rowDetail.expandedIds);
    if (open) next.add(id);
    else next.delete(id);
    rowDetail.onExpandedChange(next);
  };
  /** The header "±": open every row of the filtered list, or close them all when
   *  every one is already open. */
  const toggleAllDetails = () => {
    if (!rowDetail || !getRowId) return;
    const ids = sorted.map((r, i) => getRowId(r, i));
    const allOpen = ids.length > 0 && ids.every((id) => rowDetail.expandedIds.has(id));
    rowDetail.onExpandedChange(allOpen ? new Set() : new Set(ids));
  };

  // ── Keyboard row navigation (mouse-free operation, ADR-0013 / CLAUDE.md §7.1) ─
  // Enabled ONLY for interactive grids (row click / double-click / selectable) so
  // purely-display grids are visually and behaviourally unchanged and add no extra
  // Tab stop. Uses a ROVING tabindex: exactly one row is tabbable at a time, so the
  // grid is a SINGLE Tab stop; ↑/↓ move the active row (crossing pages at the
  // edges), Home/End jump within the page, PageUp/PageDown page, Enter opens the
  // row (double-click action, else click), and Space toggles its selection.
  const keyboardNav = !!(onRowClick || onRowDoubleClick || selectable);
  const [activeIndex, setActiveIndex] = useState(-1);
  // Whether the user is actively driving this grid with the KEYBOARD. Starts false
  // and flips true only on a real navigation keydown inside the grid; a mouse press
  // clears it. The visible orange active-row/-cell ring is gated on this (CSS class
  // `.adg-kbd`), so the ring NEVER appears on the programmatic auto-focus that fires
  // when the grid loads its first row (Chrome applies :focus-visible to that focus
  // when the last interaction was keyboard, so :focus-visible alone wasn't enough —
  // user report "orange first row on page open", 2026-09-28), and never on a mouse
  // click — only during genuine ↑/↓/Tab navigation. Keeps keyboard-nav feedback
  // (rule 14).
  const [kbdActive, setKbdActive] = useState(false);
  // Cell-by-cell Tab navigation (ARIA grid model, user request 2026-09-28): Tab
  // moves focus to the next DATA column in the same row and wraps to the first
  // cell of the next row at the end; Shift+Tab reverses; ↑/↓ still move rows but
  // KEEP the active column. The active column is read from the focused cell's
  // data-adg-col marker at keydown time (no separate state needed).
  const rowRefs = useRef<(HTMLTableRowElement | null)[]>([]);
  // Per-row cell elements, indexed [rowIndex][dataColIndex] — the DATA cells only
  // (the checkbox / row-edit action columns are not Tab targets). Populated by the
  // <td> ref callbacks so a Tab / arrow move can programmatically focus a cell.
  const cellRefs = useRef<(HTMLTableCellElement | null)[][]>([]);
  // Root element — used to tell whether focus is currently inside THIS grid
  // (so an auto-refocus doesn't disrupt a row the user is already navigating).
  const rootRef = useRef<HTMLDivElement>(null);
  // Set when a keyboard move crosses a page boundary, so the effect below can
  // move focus to the first / last row once the new page's rows have rendered.
  const pendingFocusRef = useRef<null | "first" | "last">(null);
  // The data-column to land on after a cross-page move (−1 = focus the row).
  const pendingColRef = useRef<number>(-1);

  const focusRow = (idx: number) => {
    const el = rowRefs.current[idx];
    if (el) {
      el.focus();
      el.scrollIntoView?.({ block: "nearest" });
    }
  };

  // Focus one data cell (the active column is then read from its data-adg-col).
  const focusCell = (idx: number, ci: number) => {
    const el = cellRefs.current[idx]?.[ci];
    if (el) {
      setActiveIndex(idx);
      el.focus();
      el.scrollIntoView?.({ block: "nearest", inline: "nearest" });
    } else focusRow(idx);
  };

  // The data-column index of the currently-focused element (via the cell's
  // data-adg-col marker), or −1 when the row itself / a non-data cell holds focus.
  const currentColOf = (t: HTMLElement): number => {
    const td = t.closest("td[data-adg-col]") as HTMLElement | null;
    if (!td) return -1;
    const n = Number(td.getAttribute("data-adg-col"));
    return Number.isNaN(n) ? -1 : n;
  };

  // Move focus to the nearest focusable control just OUTSIDE this grid, in the
  // given direction — used when ↓ is pressed on the very LAST row of the LAST page
  // (dir 'next') or ↑ on the very FIRST row of the FIRST page (dir 'prev'), so
  // keyboard nav "escapes" the grid onto the next / previous form control instead
  // of dead-ending at the boundary (user request 2026-09-21). Scoped to the form
  // content region so it never jumps into the sidebar.
  const FOCUSABLE_OUT =
    'a[href],button:not([disabled]),input:not([disabled]):not([type=hidden]),select:not([disabled]),textarea:not([disabled]),[contenteditable="true"],[tabindex]:not([tabindex="-1"])';
  const moveFocusOutOfGrid = (dir: "next" | "prev"): void => {
    const root = rootRef.current;
    if (!root) return;
    const scope = (root.closest(".content") as HTMLElement | null) ?? document.body;
    const outVisible = (el: HTMLElement): boolean => {
      for (let n: HTMLElement | null = el; n && n !== scope; n = n.parentElement) {
        if (n.hidden) return false;
        const st = n.style;
        if (st && (st.display === "none" || st.visibility === "hidden")) return false;
      }
      return true;
    };
    const cands = Array.from(scope.querySelectorAll<HTMLElement>(FOCUSABLE_OUT)).filter(
      (el) =>
        !root.contains(el) && !el.closest(".sidebar") && el.getAttribute("aria-hidden") !== "true" && outVisible(el),
    );
    if (cands.length === 0) return;
    if (dir === "next") {
      const t = cands.find((el) => !!(root.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING));
      if (t) {
        t.focus();
        t.scrollIntoView?.({ block: "nearest" });
      }
    } else {
      let prev: HTMLElement | null = null;
      for (const el of cands) {
        // cands are in DOM order
        if (root.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_PRECEDING) prev = el;
        else break;
      }
      if (prev) {
        prev.focus();
        prev.scrollIntoView?.({ block: "nearest" });
      }
    }
  };

  // Clamp the active row to the current page, and apply a pending cross-page focus.
  useEffect(() => {
    if (!keyboardNav) return;
    if (pendingFocusRef.current) {
      const idx = pendingFocusRef.current === "last" ? pageRows.length - 1 : 0;
      const col = pendingColRef.current;
      pendingFocusRef.current = null;
      pendingColRef.current = -1;
      if (idx >= 0) {
        setActiveIndex(idx);
        if (col >= 0) focusCell(idx, col);
        else focusRow(idx);
      }
      return;
    }
    setActiveIndex((i) => (pageRows.length === 0 ? -1 : i < 0 ? -1 : Math.min(i, pageRows.length - 1)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageRows, keyboardNav]);

  // Auto-focus the grid so it is keyboard-drivable without hunting for focus with
  // Tab (user request 2026-09-21): the first row on form OPEN, AND re-focus after a
  // data RELOAD (Refresh / status-tab / re-query) so keyboard nav survives a
  // refresh instead of dropping to the page top. Keyed on the caller's `rows` — a
  // genuine data change — NOT on internal sort/filter/pagination (those keep the
  // same `rows`). Three guards keep it unobtrusive: only keyboard-navigable grids;
  // never steal focus from an editable field the user is in (a search box, etc.);
  // and never disrupt a row the user is already navigating (focus already inside
  // this grid). It keeps the current active row across a reload (falls back to the
  // first). preventScroll avoids a jump on long pages.
  useEffect(() => {
    if (!keyboardNav || !autoFocusFirstRow) return;
    if (pageRows.length === 0) return; // wait for data
    const ae = document.activeElement as HTMLElement | null;
    // Don't steal focus from a form FIELD that already holds it — a text / number
    // input, textarea, select, contenteditable, OR a combo / list (ARIA role). This
    // is what lets a form-WITH-grid (e.g. Sample Registration) keep focus on its top
    // field when the grid's data arrives LATE, instead of the grid grabbing a row
    // (user report 2026-09-21). A plain BUTTON (e.g. Refresh) is NOT a field, so
    // clicking Refresh still re-focuses the grid — the reload behaviour is unchanged.
    const onField =
      !!ae &&
      (/^(INPUT|TEXTAREA|SELECT)$/.test(ae.tagName) ||
        ae.isContentEditable ||
        ae.getAttribute("role") === "combobox" ||
        ae.getAttribute("role") === "listbox");
    if (onField) return; // a form field owns focus
    if (ae && rootRef.current?.contains(ae)) return; // user already navigating this grid
    const idx = activeIndex >= 0 && activeIndex < pageRows.length ? activeIndex : 0;
    setActiveIndex(idx);
    rowRefs.current[idx]?.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, keyboardNav, autoFocusFirstRow]);

  const activateRow = (row: T, id: string | number) => {
    if (onRowDoubleClick) onRowDoubleClick(row);
    else if (rowClickToggles) {
      toggleOne(id);
      onRowClick?.(row);
    } else onRowClick?.(row);
  };

  const onRowKeyDown = (e: ReactKeyboardEvent<HTMLTableRowElement>, i: number, row: T, id: string | number) => {
    // A keydown reaching a focused row means the user is driving the grid by
    // keyboard — reveal the active-row/-cell ring (gated by `.adg-kbd`) from here
    // on. (Cleared again by a mouse press on the grid; see onRootMouseDown.)
    if (!kbdActive) setKbdActive(true);
    // Master–detail: "+" opens the focused row's detail, "−" closes it. Only when
    // the ROW itself has focus, so a cell editor keeps both characters.
    if (rowDetail && e.target === e.currentTarget && (e.key === "+" || e.key === "=" || e.key === "-")) {
      e.preventDefault();
      setDetailOpen(id, e.key !== "-");
      return;
    }
    // Keydown bubbles up from whatever is focused inside the row. Decide what the
    // focused control is allowed to keep:
    //  • an inline TEXT editor (text input / textarea / select / contenteditable)
    //    owns EVERY key — never navigate;
    //  • a focused button / link / checkbox keeps Enter + Space (native
    //    activate / toggle), but arrow + paging keys STILL navigate the rows, so
    //    "tick a checkbox, then arrow down" works.
    const t = e.target as HTMLElement;
    if (t !== e.currentTarget) {
      const tag = t.tagName;
      const isTextEntry =
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        t.isContentEditable ||
        (tag === "INPUT" && !/^(checkbox|radio|button|submit|reset)$/i.test((t as HTMLInputElement).type || ""));
      if (isTextEntry) return;
      if (
        (e.key === "Enter" || e.key === " " || e.key === "Spacebar") &&
        (tag === "BUTTON" || tag === "A" || tag === "INPUT")
      )
        return;
    }
    // Preserve the active data-column across vertical moves once the user has
    // Tabbed into a cell (−1 = still row-level).
    const col = currentColOf(t);
    const toRowOrCell = (idx: number) => {
      if (col >= 0) focusCell(idx, col);
      else focusRow(idx);
    };
    switch (e.key) {
      case "Tab": {
        // Cell-by-cell across the DATA columns (ARIA grid model, user request
        // 2026-09-28). A focused text / inline editor owns Tab (it returned above
        // via the isTextEntry guard). Not preventing default at the true grid edge
        // lets Tab / Shift+Tab escape onto the next / previous form control.
        const ncols = visibleColumns.length;
        if (ncols === 0) break;
        if (!e.shiftKey) {
          if (col < 0) {
            e.preventDefault();
            focusCell(i, 0);
          } else if (col < ncols - 1) {
            e.preventDefault();
            focusCell(i, col + 1);
          } else if (i < pageRows.length - 1) {
            e.preventDefault();
            focusCell(i + 1, 0);
          } else if (safePage < totalPages) {
            e.preventDefault();
            pendingFocusRef.current = "first";
            pendingColRef.current = 0;
            setPage(safePage + 1);
          }
        } else {
          if (col > 0) {
            e.preventDefault();
            focusCell(i, col - 1);
          } else if (col === 0 && i > 0) {
            e.preventDefault();
            focusCell(i - 1, ncols - 1);
          } else if (col === 0 && safePage > 1) {
            e.preventDefault();
            pendingFocusRef.current = "last";
            pendingColRef.current = ncols - 1;
            setPage(safePage - 1);
          }
        }
        break;
      }
      case "ArrowDown":
        e.preventDefault();
        if (i < pageRows.length - 1) {
          setActiveIndex(i + 1);
          toRowOrCell(i + 1);
        } else if (safePage < totalPages) {
          pendingFocusRef.current = "first";
          pendingColRef.current = col;
          setPage(safePage + 1);
        } else moveFocusOutOfGrid("next"); // last row of last page → next control
        break;
      case "ArrowUp":
        e.preventDefault();
        if (i > 0) {
          setActiveIndex(i - 1);
          toRowOrCell(i - 1);
        } else if (safePage > 1) {
          pendingFocusRef.current = "last";
          pendingColRef.current = col;
          setPage(safePage - 1);
        } else moveFocusOutOfGrid("prev"); // first row of first page → prev control
        break;
      case "Home":
        e.preventDefault();
        setActiveIndex(0);
        toRowOrCell(0);
        break;
      case "End": {
        e.preventDefault();
        const last = pageRows.length - 1;
        setActiveIndex(last);
        toRowOrCell(last);
        break;
      }
      case "PageDown":
        if (safePage < totalPages) {
          e.preventDefault();
          pendingFocusRef.current = "first";
          setPage(safePage + 1);
        }
        break;
      case "PageUp":
        if (safePage > 1) {
          e.preventDefault();
          pendingFocusRef.current = "first";
          setPage(safePage - 1);
        }
        break;
      case "Enter":
        e.preventDefault();
        activateRow(row, id);
        break;
      case " ":
      case "Spacebar":
        // A grouped grid's keyboard rows are group headings, which are not selectable.
        if ((leadCb || rowClickToggles) && !groupedRows) {
          e.preventDefault();
          toggleOne(id);
        }
        break;
      default:
        break;
    }
  };

  // Roving tabindex: the active row is the single tabstop; before the user has
  // moved, the first row carries it so a Tab lands on the grid.
  const rowTabIndex = (i: number): number | undefined =>
    !keyboardNav ? undefined : activeIndex < 0 ? (i === 0 ? 0 : -1) : i === activeIndex ? 0 : -1;

  // ── Frozen (pinned) leading columns (opt-in via `stickyColumns`) ──────────
  // The pinned DATA column's left offset = the checkbox column width (when
  // selectable) + the widths of every pinned column before it.  Because the grid
  // uses AUTO table-layout, a column's RENDERED width can differ from its
  // configured `width`, so summing config alone drifts and the frozen columns
  // overlap the scrolling ones.  We MEASURE the real header-cell widths (layout
  // effect below) and stack those; the configured widths are a first-paint
  // fallback only.
  const CB_STICKY_WIDTH = 34; // matches .adg-cb-col

  // ROW-level inline edit (legacy InlineEditMode="Inline"). `editRowId` is the
  // row currently open for editing; `rowDraft` holds its pending values keyed by
  // column field. Both are inert unless the caller passed `rowEdit`.
  const [editRowId, setEditRowId] = useState<string | number | null>(null);
  const [rowDraft, setRowDraft] = useState<Record<string, string>>({});
  const [rowSaving, setRowSaving] = useState(false);

  const beginRowEdit = (row: T, id: string | number) => {
    const d: Record<string, string> = {};
    for (const c of columns) {
      if (!c.edit) continue;
      const raw = c.value ? c.value(row) : (row as Record<string, unknown>)[c.field];
      d[c.field] = raw == null ? "" : String(raw);
    }
    setRowDraft(d);
    setEditRowId(id);
  };
  const cancelRowEdit = () => {
    setEditRowId(null);
    setRowDraft({});
  };
  const commitRowEdit = async (row: T) => {
    if (!rowEdit) return;
    setRowSaving(true);
    try {
      await rowEdit.onSave(row, rowDraft);
      setEditRowId(null);
      setRowDraft({});
    } catch {
      // Keep the row open so the user can correct it — the caller surfaces why.
    } finally {
      setRowSaving(false);
    }
  };
  const headRowRef = useRef<HTMLTableRowElement>(null);
  const [measuredLefts, setMeasuredLefts] = useState<number[]>([]);
  const stickyWidthOf = (col: AppColumn<T> | undefined): number => {
    if (!col) return 140;
    const w = effectiveWidth(col);
    return typeof w === "number" ? w : 140;
  };
  const stickyLeftOf = (index: number): number | undefined => {
    if (index >= stickyColumns) return undefined;
    if (measuredLefts.length > index) return measuredLefts[index]!; // measured — accurate
    let left = showCheckboxColumn ? CB_STICKY_WIDTH : 0; // first-paint fallback
    for (let k = 0; k < index; k++) left += stickyWidthOf(visibleColumns[k]);
    return left;
  };
  const cbSticky = stickyColumns > 0;

  // Measure the real pinned-column left offsets from the rendered header cells
  // (getBoundingClientRect().width is unaffected by the sticky position, so it
  // reflects the true column width).  Re-runs when the columns, data, page or a
  // manual resize could have changed a width — and on window resize.
  useLayoutEffect(() => {
    if (stickyColumns <= 0) {
      setMeasuredLefts((prev) => (prev.length ? [] : prev));
      return;
    }
    const rowEl = headRowRef.current;
    if (!rowEl) return;
    const measure = () => {
      const cells = Array.from(rowEl.children) as HTMLElement[];
      const dataStart = showCheckboxColumn ? 1 : 0;
      const lefts: number[] = [];
      let acc = showCheckboxColumn ? (cells[0]?.getBoundingClientRect().width ?? 0) : 0;
      for (let i = 0; i < stickyColumns; i++) {
        lefts[i] = acc;
        acc += cells[dataStart + i]?.getBoundingClientRect().width ?? 0;
      }
      setMeasuredLefts((prev) =>
        prev.length === lefts.length && prev.every((v, i) => Math.abs(v - lefts[i]!) < 0.5) ? prev : lefts,
      );
    };
    measure();
    // Re-measure on ANY size change (auto table-layout can resize a column after
    // paint — fonts, async content, container resize — which the deps below don't
    // catch).  Observe each header cell so an individual column widening re-stacks
    // the pinned offsets immediately (fixes frozen/scrolled column overlap).
    const ro = new ResizeObserver(measure);
    ro.observe(rowEl);
    for (const c of Array.from(rowEl.children)) ro.observe(c as Element);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [stickyColumns, showCheckboxColumn, visibleColumns, colWidths, pageRows, safePage, pageSize]);

  return (
    <div
      ref={rootRef}
      className={`adg${className ? ` ${className}` : ""}${flushRight ? " adg-flush" : ""}${maximized ? " adg-maximized" : ""}${kbdActive ? " adg-kbd" : ""}${accentSelection ? " adg-accent-sel" : ""}`}
      onMouseDownCapture={kbdActive ? () => setKbdActive(false) : undefined}
    >
      {!hideHeaderTools && (maximizable || headerLeft || (headerExport && headerExport.headers.length > 0)) && (
        <div className={`adg-header${headerLeft ? " adg-header--custom" : ""}`}>
          {gridTitle && <span className="adg-header-title">{gridTitle}</span>}
          {headerLeft}
          <span className="adg-header-sp" />
          {!hideHeaderExport && headerExport && headerExport.headers.length > 0 && (
            <ExportMenu
              iconOnly
              triggerClassName="adg-max-btn"
              filename={effExportFilename}
              headers={headerExport.headers}
              rows={headerExport.rows}
            />
          )}
          {maximizable && (
            <button
              type="button"
              className="adg-max-btn"
              onClick={() => setMaximized(!maximized)}
              title={maximized ? "Restore (Esc)" : "Maximize"}
              aria-label={maximized ? "Restore" : "Maximize"}
            >
              <i className={`ti ${maximized ? "ti-arrows-minimize" : "ti-arrows-maximize"}`} aria-hidden="true" />
            </button>
          )}
        </div>
      )}
      <div className="adg-scroll">
        <table ref={tableRef} className={`adg-table${fixedLayout ? " adg-fixed" : ""}${wrapHeaders ? " adg-wrap-headers" : ""}`}>
          {/* fixedLayout: a proportional <colgroup> turns each column's configured
              px width into a PERCENTAGE of the total, so `table-layout: fixed`
              + `width: 100%` scales every column to exactly fill the container —
              all columns stay in view (no horizontal scroll, no cut-off right
              column); columns shrink proportionally (ellipsis) when space is
              tight, grow to fill when there's room. */}
          {fixedLayout &&
            (() => {
              const px = (w: number | string | undefined, dflt: number) =>
                typeof w === "number"
                  ? w
                  : w != null && !Number.isNaN(parseFloat(String(w)))
                    ? parseFloat(String(w))
                    : dflt;
              const cbW = showCheckboxColumn ? 34 : 0;
              const exW = rowDetail ? 30 : 0;
              const widths = visibleColumns.map((c) => px(autoWidths[c.field] ?? c.width, 120));
              const total = cbW + exW + widths.reduce((a, b) => a + b, 0);
              const pct = (w: number) => `${((w / total) * 100).toFixed(4)}%`;
              return (
                <colgroup>
                  {leadCb && <col style={{ width: pct(cbW) }} />}
                  {rowDetail && <col style={{ width: pct(exW) }} />}
                  {groupCb && <col style={{ width: pct(cbW) }} />}
                  {visibleColumns.map((c) => (
                    <col key={c.field} style={{ width: pct(px(autoWidths[c.field] ?? c.width, 120)) }} />
                  ))}
                </colgroup>
              );
            })()}
          <thead>
            <tr ref={headRowRef}>
              {leadCb && (
                <th className={`adg-cb-col${cbSticky ? " adg-sticky" : ""}`} style={cbSticky ? { left: 0 } : undefined}>
                  <input
                    type="checkbox"
                    checked={allChecked}
                    ref={(el) => {
                      if (el) el.indeterminate = someChecked && !allChecked;
                    }}
                    onChange={toggleAll}
                    aria-label="Select all rows"
                  />
                </th>
              )}
              {/* Row-edit pencil column (legacy InlineEditMode="Inline"). It sits
                  AFTER the selection checkbox, matching the legacy grid. */}
              {rowEdit && <th className="adg-rowedit-col" aria-label="Edit" />}
              {/* Master–detail: "±" opens / closes every row of the list. */}
              {rowDetail && (
                <th className="adg-expand-col">
                  {!rowDetail.hideExpandAll && (
                  <button
                    type="button"
                    className="adg-expand-btn"
                    title="Expand / collapse all"
                    aria-label="Expand or collapse all rows"
                    onClick={toggleAllDetails}
                  >
                    ±
                  </button>
                  )}
                </th>
              )}
              {groupCb && (
                <th className="adg-cb-col">
                  <input
                    type="checkbox"
                    checked={allChecked}
                    ref={(el) => {
                      if (el) el.indeterminate = someChecked && !allChecked;
                    }}
                    onChange={toggleAll}
                    aria-label="Select all rows"
                  />
                </th>
              )}
              {visibleColumns.map((col, ci) => {
                const sortable = col.sortable !== false;
                const filterable = showFilters && col.filterable !== false && !!col.header;
                const isSorted = sorter.sort.key === col.field && sorter.sort.dir !== null;
                const caret = !isSorted ? "⇅" : sorter.sort.dir === "asc" ? "▲" : "▼";
                const stickyLeft = stickyLeftOf(ci);
                const isSticky = stickyLeft !== undefined;
                const isStickyEdge = isSticky && ci === stickyColumns - 1;
                const dragging = dragField === col.field;
                const dropTarget = dragOverField === col.field && dragField !== null && dragField !== col.field;
                return (
                  <th
                    key={col.field}
                    className={`adg-th${sortable ? " adg-th-sortable" : ""}${col.type === "number" ? " adg-th-num" : ""}${isSticky ? " adg-sticky" : ""}${isStickyEdge ? " adg-sticky-edge" : ""}${dragging ? " adg-th-dragging" : ""}${dropTarget ? " adg-th-dropbefore" : ""}`}
                    // A user-dragged width must also pin minWidth (else the width:100%
                    // AUTO-layout table just squeezes the column back to content width and
                    // the resize is invisible — the ModuleListView grids set both, which is
                    // why THEY resize and this one didn't until now, 2026-08-05).
                    // An auto-fit column pins width + min + max so the auto table-layout
                    // renders exactly the measured width (never squeezed, never wider).
                    style={(() => {
                      const dragged = colWidths[col.field];
                      const auto = autoWidths[col.field];
                      // enforceWidths: pin a numeric-width column to its exact width
                      // (unless the user dragged it, or it auto-fits) so the AUTO
                      // table-layout can't grow it to fit the header/content.
                      const pin = dragged === undefined && auto === undefined && enforceWidths
                        && typeof col.width === "number" ? col.width : undefined;
                      const maxW = dragged !== undefined ? undefined
                        : auto ?? pin ?? col.maxWidth;
                      return {
                        width: effectiveWidth(col),
                        minWidth: dragged ?? auto ?? pin ?? col.minWidth,
                        ...(maxW !== undefined ? { maxWidth: maxW } : {}),
                        ...(isSticky ? { left: stickyLeft } : {}),
                      };
                    })()}
                    onClick={sortable ? () => sorter.setSort(col.field) : undefined}
                    // Right-click → the shared header context menu (Sort · Hide ·
                    // Show All · Chooser). preventDefault suppresses the native menu.
                    onContextMenu={(e) => {
                      e.preventDefault();
                      setHeaderMenu({ field: col.field, x: e.clientX, y: e.clientY });
                    }}
                    // Drag-reorder: dragging a header drops it before the target
                    // column. Guarded by resizingRef so grabbing the resize handle
                    // never starts a reorder.
                    draggable
                    onDragStart={(e) => {
                      if (resizingRef.current) {
                        e.preventDefault();
                        return;
                      }
                      setDragField(col.field);
                      e.dataTransfer.effectAllowed = "move";
                      try {
                        e.dataTransfer.setData("text/plain", col.field);
                      } catch {
                        /* older browsers */
                      }
                    }}
                    onDragOver={(e) => {
                      if (dragField && dragField !== col.field) {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "move";
                        if (dragOverField !== col.field) setDragOverField(col.field);
                      }
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (dragField) reorderColumns(dragField, col.field);
                      setDragField(null);
                      setDragOverField(null);
                    }}
                    onDragEnd={() => {
                      setDragField(null);
                      setDragOverField(null);
                    }}
                  >
                    <span className="adg-th-inner">
                      <span className="adg-th-label">{col.header}</span>
                      {sortable && <span className={`adg-sort${isSorted ? " active" : ""}`}>{caret}</span>}
                      {filterable && (
                        /* Excel-style multi-select header filter (funnel/chevron
                           → popup) — same UX as every legacy grid. */
                        <ColFilterButton<T>
                          variant="inline"
                          col={col.field}
                          label={col.headerText ?? (typeof col.header === "string" ? col.header : undefined)}
                          rows={cascadeRows(rows, filters, col.field, cascadeText)}
                          formatter={(r) => displayText(r, col)}
                          /* Order the filter value list to MATCH the column's
                             sort: the actively click-sorted column uses its own
                             direction; every other column falls back to the
                             grid's default (DESC for an ID-first grid, else ASC).
                             Ordered by the raw value so date/number sort by real
                             value, not string. */
                          sortDir={sorter.sort.key === col.field ? sorter.sort.dir : defaultFilterDir}
                          sortKey={(r) => sortValue(r, col)}
                          value={filters[col.field] ?? null}
                          onChange={(v) => setColFilter(col.field, v)}
                        />
                      )}
                    </span>
                    {/* Drag handle — every column is resizable. */}
                    <span
                      className="adg-resize"
                      onMouseDown={(e) => startResize(col.field, e)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </th>
                );
              })}
            </tr>
            {filterRow && (
              <tr className="adg-filter-row">
                {leadCb && <th className={`adg-cb-col${cbSticky ? " adg-sticky" : ""}`} style={cbSticky ? { left: 0 } : undefined} />}
                {rowEdit && <th className="adg-rowedit-col" />}
                {rowDetail && <th className="adg-expand-col" />}
                {groupCb && <th className="adg-cb-col" />}
                {visibleColumns.map((col, ci) => {
                  const stickyLeft = stickyLeftOf(ci);
                  const label = typeof col.header === "string" ? col.header : (col.headerText ?? col.field);
                  return (
                    <th key={col.field}
                        className={stickyLeft !== undefined ? "adg-sticky" : undefined}
                        style={stickyLeft !== undefined ? { left: stickyLeft } : undefined}>
                      {col.filterable !== false && !!col.header && (
                        <input
                          type="text"
                          className="adg-filter-input"
                          value={rowFilterText[col.field] ?? ""}
                          onChange={(e) => {
                            const v = e.target.value;
                            setRowFilterText((cur) => ({ ...cur, [col.field]: v }));
                          }}
                          // A filter is not an edit (standing rule 15), and its keys
                          // belong to the box, not to the grid's row navigation.
                          data-unsaved-ignore=""
                          onKeyDown={(e) => e.stopPropagation()}
                          aria-label={`Search ${label}`}
                          placeholder="Filter…"
                        />
                      )}
                    </th>
                  );
                })}
              </tr>
            )}
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colCount}>
                  <div className="adg-state">
                    <LoaderSymbol size={20} /> {loadingText}
                  </div>
                </td>
              </tr>
            ) : pageRows.length === 0 ? (
              <tr>
                <td colSpan={colCount}>
                  {/* Bare text, no icon — ALPACA parity (owner compared the two
                      products 2026-09-24); its empty data row is just the message. */}
                  <div className="adg-state empty">{emptyText}</div>
                </td>
              </tr>
            ) : (
              pageRows.map((row, i) => {
                const id = getRowId ? getRowId(row, i) : (safePage - 1) * pageSize + i;
                const isSel =
                  (selectedRowId != null && id === selectedRowId) ||
                  (selectable && !!selectedIds && selectedIds.has(id));
                const hasRowAction = !!(onRowClick || rowClickToggles);
                const onRowClickHandler = keyboardNav
                  ? (e: ReactMouseEvent<HTMLTableRowElement>) => {
                      // Establish keyboard focus on the ROW so the arrow keys work —
                      // browsers don't reliably focus a <tr> when one of its cells is
                      // clicked, so focus it explicitly (unless a control inside the
                      // row — checkbox, button, link, inline editor — owns the click).
                      const tgt = e.target as HTMLElement;
                      if (!tgt.closest('input,button,a,select,textarea,[contenteditable="true"]')) {
                        rowRefs.current[i]?.focus();
                      }
                      setActiveIndex(i);
                      if (rowClickToggles) {
                        toggleOne(id);
                        onRowClick?.(row);
                      } else onRowClick?.(row);
                    }
                  : // keyboardNav is false here ⟹ no onRowClick / selectable ⟹ no handler.
                    undefined;
                const isActive = keyboardNav && i === activeIndex;
                const detailOpen = !!rowDetail && rowDetail.expandedIds.has(id);
                return (
                  <Fragment key={id}>
                  <tr
                    ref={(el) => {
                      rowRefs.current[i] = el;
                    }}
                    tabIndex={rowTabIndex(i)}
                    className={`${isSel ? "adg-selected" : ""}${isActive ? " adg-active" : ""}${keyboardNav ? " adg-nav" : ""}${hasRowAction || onRowDoubleClick ? " adg-row-click" : ""}${detailOpen ? " adg-detail-open" : ""}${getRowClassName ? ` ${getRowClassName(row) ?? ""}` : ""}`}
                    onClick={onRowClickHandler}
                    onDoubleClick={onRowDoubleClick ? () => onRowDoubleClick(row) : undefined}
                    onMouseEnter={onRowHover ? () => onRowHover(row) : undefined}
                    onFocus={keyboardNav ? () => setActiveIndex(i) : undefined}
                    onKeyDown={keyboardNav ? (e) => onRowKeyDown(e, i, row, id) : undefined}
                  >
                    {leadCb && (
                      <td
                        className={`adg-cb-col${cbSticky ? " adg-sticky" : ""}`}
                        style={cbSticky ? { left: 0 } : undefined}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {/* While a row is OPEN for inline editing its selection
                            checkbox is hidden — legacy XAF does the same, because a
                            row being edited is not a row you are selecting to act
                            on (user report 2026-09-24). */}
                        {rowEdit && editRowId === id ? null : (
                          <input
                            type="checkbox"
                            checked={!!selectedIds && selectedIds.has(id)}
                            onChange={() => toggleOne(id)}
                            aria-label="Select row"
                            // In a keyboard-navigable grid the row checkboxes are NOT
                            // individual Tab stops (that made Tab walk row-by-row) —
                            // the grid is a single Tab stop and Space on the focused
                            // row toggles selection. Mouse click still works.
                            tabIndex={keyboardNav ? -1 : undefined}
                          />
                        )}
                      </td>
                    )}
                    {rowEdit &&
                      (() => {
                        const editing = editRowId === id;
                        const off = rowEdit.disabled?.(row);
                        return (
                          <td className="adg-rowedit-col" onClick={(e) => e.stopPropagation()}>
                            {off ? null : editing ? (
                              <span className="adg-rowedit-actions">
                                <button
                                  type="button"
                                  className="adg-rowedit-btn"
                                  title="Save"
                                  disabled={rowSaving}
                                  onClick={() => {
                                    void commitRowEdit(row);
                                  }}
                                >
                                  <i className="ti ti-device-floppy" aria-hidden="true" />
                                </button>
                                <button
                                  type="button"
                                  className="adg-rowedit-btn"
                                  title="Cancel"
                                  disabled={rowSaving}
                                  onClick={cancelRowEdit}
                                >
                                  <i className="ti ti-arrow-back-up" aria-hidden="true" />
                                </button>
                              </span>
                            ) : (
                              <button
                                type="button"
                                className="adg-rowedit-btn"
                                title="Edit"
                                onClick={() => beginRowEdit(row, id)}
                              >
                                <i className="ti ti-pencil" aria-hidden="true" />
                              </button>
                            )}
                          </td>
                        );
                      })()}
                    {rowDetail && (
                      <td className="adg-expand-col" onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="adg-expand-btn"
                          title={detailOpen ? "Collapse" : (rowDetail.expandTitle ?? "Show details")}
                          aria-label={detailOpen ? "Collapse row" : "Expand row"}
                          aria-expanded={detailOpen}
                          // Like the row checkboxes: not an extra Tab stop in a
                          // keyboard-navigable grid ("+" / "−" on the row do it).
                          tabIndex={keyboardNav ? -1 : undefined}
                          onClick={() => setDetailOpen(id, !detailOpen)}
                        >
                          {detailOpen ? "−" : "+"}
                        </button>
                      </td>
                    )}
                    {/* A group heading is not selectable (G5): its checkbox cell stays empty. */}
                    {groupCb && <td className="adg-cb-col" />}
                    {rowDetail?.groupLabel ? (
                      // G5 grouped grid: the group heading spans every column.
                      <td colSpan={visibleColumns.length} className="adg-group-label">
                        {rowDetail.groupLabel(row)}
                      </td>
                    ) : visibleColumns.map((col, ci) => {
                      const align = col.align ?? (col.type === "number" ? "right" : undefined);
                      const stickyLeft = stickyLeftOf(ci);
                      const isSticky = stickyLeft !== undefined;
                      const isStickyEdge = isSticky && ci === stickyColumns - 1;
                      // A required-yet-empty editable cell tints the WHOLE <td>
                      // flat (edge-to-edge, like legacy ALPACA) rather than a
                      // rounded inner box floating inside the cell padding.
                      const reqEmpty = !!col.edit?.requiredEmpty?.(row);
                      const cls =
                        (align === "right" ? "adg-td-num" : align === "center" ? "adg-td-center" : "") +
                        (isSticky ? " adg-sticky" : "") +
                        (isStickyEdge ? " adg-sticky-edge" : "") +
                        (col.wrap ? " adg-wrap" : "") +
                        (col.noClip ? " adg-noclip" : "") +
                        (col.autoFit ? " adg-autofit" : "") +
                        (reqEmpty ? " adg-req-empty" : "") +
                        (col.cellClass?.(row) ? " " + col.cellClass(row) : "");
                      let content: ReactNode;
                      if (rowEdit && col.edit && editRowId === id) {
                        // This row is OPEN for editing: draw a live editor bound
                        // to the draft. Unlike the per-cell mode below, nothing
                        // commits until the row's Save is pressed.
                        const ec = col.edit;
                        const opts = typeof ec.options === "function" ? ec.options(row) : ec.options;
                        const v = rowDraft[col.field] ?? "";
                        const set = (nv: string) => setRowDraft((d) => ({ ...d, [col.field]: nv }));
                        content =
                          ec.type === "combo" ? (
                            <select className="adg-rowedit-input" value={v} onChange={(e) => set(e.target.value)}>
                              <option value="" />
                              {(opts ?? []).map((o) => (
                                <option key={o} value={o}>
                                  {o}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <input
                              className="adg-rowedit-input"
                              type={ec.type === "number" ? "number" : "text"}
                              value={v}
                              onChange={(e) => set(e.target.value)}
                            />
                          );
                      } else if (rowEdit && col.edit) {
                        // Row-edit grids are NOT click-to-edit at rest — a cell
                        // outside the open row renders as plain text.
                        content = col.render ? col.render(row) : displayText(row, col);
                      } else if (col.edit && onCellCommit) {
                        // Opt-in inline editing (§9) — the shared click-to-edit
                        // cell. No editor drawn at rest; commits via onCellCommit.
                        const ec = col.edit;
                        const raw = col.value ? col.value(row) : (row as Record<string, unknown>)[col.field];
                        const strVal = raw == null ? "" : String(raw);
                        const opts = typeof ec.options === "function" ? ec.options(row) : ec.options;
                        content = (
                          <InlineCellEditor
                            value={strVal}
                            type={ec.type}
                            options={opts}
                            trigger={ec.trigger}
                            disabled={ec.disabled?.(row)}
                            requiredEmpty={ec.requiredEmpty?.(row)}
                            clearable={ec.clearable}
                            allowCustom={ec.allowCustom}
                            pattern={ec.pattern}
                            patternMessage={ec.patternMessage}
                            display={ec.display}
                            onCommit={(v) => onCellCommit(row, col, v)}
                            onLiveChange={onCellChange ? (v) => onCellChange(row, col, v) : undefined}
                          />
                        );
                      } else if (col.render) content = col.render(row);
                      else if (col.type === "status") {
                        const label = displayText(row, col);
                        content = label ? <span className={`adg-pill ${statusVariant(label)}`}>{label}</span> : null;
                      } else content = displayText(row, col);
                      // Global empty-cell rule (user request 2026-07-10): a cell whose
                      // rendered content is just a typographic DASH placeholder ('—' /
                      // '–') shows BLANK — no grid shows a dash for empty data.  Real
                      // values are never a bare em/en-dash, so this is safe app-wide and
                      // spares every column from hand-coding the empty case.
                      if (content === "—" || content === "–") content = "";
                      return (
                        <td
                          key={col.field}
                          // Cell-by-cell Tab nav: each DATA cell is a roving tabindex
                          // target (−1, so it's programmatically focusable but not an
                          // extra native Tab stop) and carries its column index.
                          ref={keyboardNav ? (el) => { (cellRefs.current[i] ??= [])[ci] = el; } : undefined}
                          tabIndex={keyboardNav ? -1 : undefined}
                          data-adg-col={keyboardNav ? ci : undefined}
                          onFocus={keyboardNav ? (e) => { if (e.target === e.currentTarget) setActiveIndex(i); } : undefined}
                          className={cls.trim()}
                          style={isSticky ? { left: stickyLeft } : undefined}
                          title={typeof content === "string" ? content : undefined}
                          onContextMenu={
                            col.onCellContextMenu
                              ? (e) => {
                                  e.preventDefault();
                                  col.onCellContextMenu!(e, row);
                                }
                              : undefined
                          }
                        >
                          {content}
                        </td>
                      );
                    })}
                  </tr>
                  {detailOpen && rowDetail!.childRows
                    ? rowDetail!.childRows(row).map((child, ci2) => (
                        // G5 grouped grid: the open group's rows, under the grid's own columns.
                        <tr key={`${String(id)}::${ci2}`} className="adg-child-row">
                          {rowEdit && <td className="adg-rowedit-col" />}
                          <td className="adg-expand-col" />
                          {groupCb && (() => {
                            const childId = rowKey(child, ci2);
                            return (
                              <td className="adg-cb-col">
                                <input
                                  type="checkbox"
                                  checked={!!selectedIds && selectedIds.has(childId)}
                                  onChange={() => toggleOne(childId)}
                                  aria-label="Select row"
                                />
                              </td>
                            );
                          })()}
                          {visibleColumns.map((col) => {
                            const align = col.align ?? (col.type === "number" ? "right" : undefined);
                            let content: ReactNode = col.render ? col.render(child) : displayText(child, col);
                            if (content === "—" || content === "–") content = "";
                            return (
                              <td
                                key={col.field}
                                className={align === "right" ? "adg-td-num" : align === "center" ? "adg-td-center" : undefined}
                                title={typeof content === "string" ? content : undefined}
                              >
                                {content}
                              </td>
                            );
                          })}
                        </tr>
                      ))
                    : detailOpen && rowDetail!.render && (
                    <tr className="adg-detail-row">
                      <td colSpan={colCount}>{rowDetail!.render(row)}</td>
                    </tr>
                  )}
                  </Fragment>
                );
              })
            )}
          </tbody>
          {/* Grid SUMMARY row (legacy XAF's grid summary, e.g. the Pending Item
              grid's "Sum=0.00" beneath ExtPrice). Rendered only when a column
              declares `summary`, so no existing grid gains a row. Totals are
              computed over the FILTERED rows, not just the visible page — a
              per-page total would be misleading. */}
          {visibleColumns.some((c) => c.summary) && (
            <tfoot>
              <tr className="adg-summary-row">
                {showCheckboxColumn && <td className="adg-cb-col" />}
                {rowEdit && <td className="adg-rowedit-col" />}
                {rowDetail && <td className="adg-expand-col" />}
                {visibleColumns.map((col, ci) => {
                  let text = "";
                  if (col.summary === "sum") {
                    const total = sorted.reduce((acc, r) => {
                      const raw = col.value ? col.value(r) : (r as Record<string, unknown>)[col.field];
                      const n = typeof raw === "number" ? raw : Number(raw);
                      return acc + (Number.isFinite(n) ? n : 0);
                    }, 0);
                    text = `Sum=${total.toFixed(2)}`;
                  } else if (typeof col.summary === "function") {
                    text = col.summary(sorted);
                  }
                  const align = col.align ?? (col.type === "number" ? "right" : undefined);
                  return (
                    <td
                      key={`${col.field}-${ci}`}
                      className={align === "right" ? "adg-td-num" : align === "center" ? "adg-td-center" : ""}
                    >
                      {text}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* ── Header right-click menu (Sort · Hide · Show All · Column Chooser) ── */}
      {headerMenu &&
        (() => {
          const col = colByField.get(headerMenu.field);
          if (!col) return null;
          return (
            <HeaderContextMenu
              column={{ field: col.field, label: headerLabel(col), sortable: col.sortable !== false }}
              sort={sorter.sort}
              position={{ x: headerMenu.x, y: headerMenu.y }}
              onSort={(dir) => sorter.setSortExplicit(dir === null ? null : col.field, dir)}
              onHideColumn={() => hideColumn(col.field)}
              canHide={visibleColumns.length > 1}
              onShowAllColumns={showAllColumns}
              hasHiddenColumns={hidden.size > 0}
              onColumnChooser={() => setChooser({ x: headerMenu.x, y: headerMenu.y })}
              onClose={() => setHeaderMenu(null)}
            />
          );
        })()}

      {/* ── Column Chooser popup (opened from the header menu) ── */}
      {chooser && (
        <ColumnChooser
          columns={orderedColumns.map((c) => ({ field: c.field, label: headerLabel(c) }))}
          hidden={hidden}
          position={chooser}
          onToggle={toggleColumn}
          onShowAll={showAllColumns}
          onClose={() => setChooser(null)}
        />
      )}

      {/* ── Footer / pagination (Part C) ── */}
      {/* An EMPTY grid shows no footer (owner 2026-09-24, ALPACA parity). Legacy
         configures a PageSize on every ListView, yet DevExpress renders only
         "No data to display" when there are no rows - the Sample Transfer page
         shows this side by side: the empty Selected grid has no footer while the
         6-row All Samples grid below it does. "Page 1 of 1 (0 items)" plus a
         page-size dropdown is noise when there is nothing to page through. */}
      {!hideFooter && (
      <GridPager
        total={total}
        page={safePage}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        pageSizeOptions={pageSizeOptions}
        footerNote={footerNote}
      />
      )}
    </div>
  );
}
